import { LineChart } from '@mantine/charts';
import { Loader } from '@mantine/core';
import { IconExternalLink, IconChartLine } from '@tabler/icons-react';
import type {
    ContainerResponse,
    MetricName,
    MetricSeries,
} from '@xver/apis/v1/kufer/containers/model';
import { CopyButton } from '@xver/components/Atoms';
import DialogBox from '@xver/components/Atoms/DialogBox/DialogBox';
import dayjs from 'dayjs';
import { ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useContainerMetricsQuery } from '../hooks/useContainerMetricsQuery';
import CostCard from './CostCard';
import QuotaCard from './QuotaCard';
import { formatBytes } from '@xver/utils/units';
import { format as formatNumber } from '@xver/utils/number';

interface OverviewTabPanelProps {
    containerId: string;
    container: ContainerResponse;
}

interface EndpointRowProps {
    label: string;
    value?: string;
}

function toHttpsUrl(value: string): string {
    if (/^https:\/\//i.test(value)) return value;
    if (/^http:\/\//i.test(value)) return value.replace(/^http:\/\//i, 'https://');
    return `https://${value}`;
}

function EndpointRow({ label, value }: EndpointRowProps) {
    const { t } = useTranslation('containerEngineInstance');
    if (!value) {
        return (
            <>
                <div className="pb-2 text-[15px] border-b-[0.5px] border-line text-fg/68">
                    {label}
                </div>
                <div className="pb-2 text-[15px] border-b-[0.5px] border-line text-fg/68">-</div>
            </>
        );
    }
    const url = toHttpsUrl(value);
    return (
        <>
            <div className="pb-2 text-[15px] border-b-[0.5px] border-line text-fg/68">{label}</div>
            <div className="pb-2 text-[15px] border-b-[0.5px] border-line text-fg min-w-0 flex flex-wrap items-center justify-between gap-3">
                <span className="min-w-0 break-all font-mono text-sm">{url}</span>
                <div className="flex shrink-0 items-center gap-2">
                    <CopyButton value={url} ariaLabel={t('overview.copyEndpoint', { label })} />
                    <button
                        type="button"
                        aria-label={t('overview.openEndpoint', { label })}
                        title={t('overview.openEndpoint', { label })}
                        onClick={(e) => {
                            e.stopPropagation();
                            window.open(url, '_blank', 'noopener,noreferrer');
                        }}
                        className="inline-flex shrink-0 cursor-pointer items-center justify-center rounded-md border border-line bg-surface-2 px-2 py-1 text-fg/68 transition-colors hover:bg-surface-3 hover:text-fg"
                    >
                        <IconExternalLink size={14} />
                    </button>
                </div>
            </div>
        </>
    );
}

interface InfoRowProps {
    label: string;
    value: ReactNode;
}

function InfoRow({ label, value }: InfoRowProps) {
    return (
        <>
            <div className="pb-2 text-[15px] border-b-[0.5px] border-line text-fg/68">{label}</div>
            <div className="pb-2 text-[15px] border-b-[0.5px] border-line text-fg">{value}</div>
        </>
    );
}

export default function OverviewTabPanel({ containerId, container }: OverviewTabPanelProps) {
    const { t } = useTranslation('containerEngineInstance');

    const { start, end, step } = useMemo(() => {
        const nowSeconds = Math.floor(Date.now() / 1000);
        return {
            end: nowSeconds,
            start: nowSeconds - 60 * 60 * 24,
            step: '5m',
        };
    }, []);

    const {
        data: metricsData,
        isPending: isMetricsPending,
        error: metricsError,
    } = useContainerMetricsQuery({
        containerId,
        start,
        end,
        step,
        metrics: 'requests,cpu,memory',
    });

    const chartsByMetric = useMemo(() => {
        const empty: Record<MetricName, Array<{ date: string; value: number }>> = {
            requests: [],
            cpu: [],
            memory: [],
        };
        if (!metricsData?.series?.length) return empty;

        const pickFirst = (name: MetricName): MetricSeries | undefined =>
            metricsData.series.find((s) => s.metric_name === name);

        const toPoints = (s: MetricSeries | undefined) =>
            (s?.data_points ?? []).map((p) => ({
                date: new Date(p.timestamp * 1000).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                }),
                value: p.value,
            }));

        return {
            requests: toPoints(pickFirst('requests')),
            cpu: toPoints(pickFirst('cpu')),
            memory: toPoints(pickFirst('memory')),
        };
    }, [metricsData]);

    const source = container.source;
    const sourceType = source?.type;
    const endpoint = container.endpoint;

    return (
        <div className="w-full flex flex-col gap-4">
            <DialogBox
                label={t('overview.endpointTitle', 'Endpoint')}
                description={t(
                    'overview.endpointDescription',
                    'Default and custom domain URLs for this container.'
                )}
                classNames="p-4"
            >
                <div className="w-full grid grid-cols-[auto_1fr] items-center gap-4">
                    <EndpointRow
                        label={t('overview.endpointDefault', 'Default URL')}
                        value={endpoint?.default}
                    />
                    <EndpointRow
                        label={t('overview.endpointDomain', 'Custom domain')}
                        value={endpoint?.domain}
                    />
                </div>
            </DialogBox>

            <CostCard containerId={containerId} />
            <QuotaCard containerId={containerId} />

            <DialogBox
                label={t('overview.sourceTitle', 'Source configuration')}
                description={t('overview.sourceDescription', 'Where this container is built from.')}
                classNames="p-4"
            >
                <div className="w-full grid grid-cols-[auto_1fr] items-center gap-4">
                    <InfoRow
                        label={t('overview.sourceType', 'Source type')}
                        value={
                            sourceType === 'github'
                                ? 'GitHub'
                                : sourceType === 'url'
                                  ? 'Container image'
                                  : sourceType === 'inline'
                                    ? 'Inline Dockerfile'
                                    : '-'
                        }
                    />
                    {sourceType === 'url' && (
                        <>
                            <InfoRow
                                label={t('overview.sourceImage', 'Image')}
                                value={
                                    <span className="font-mono text-sm">
                                        {source?.image ?? '-'}
                                    </span>
                                }
                            />
                            <InfoRow
                                label={t('overview.sourceImageTag', 'Image tag')}
                                value={
                                    <span className="font-mono text-sm">
                                        {source?.image_tag ?? 'latest'}
                                    </span>
                                }
                            />
                        </>
                    )}
                    {sourceType === 'github' && (
                        <>
                            <InfoRow
                                label={t('overview.sourceRepo', 'Repository')}
                                value={
                                    <span className="font-mono text-sm">
                                        {/* GitHub fields aren't on ContainerSourceResponse - leave fallback */}
                                        {container.image_url ?? '-'}
                                    </span>
                                }
                            />
                        </>
                    )}
                    {sourceType === 'inline' && (
                        <InfoRow
                            label={t('overview.sourceInline', 'Dockerfile')}
                            value={t('overview.inlineDockerfile', 'Inline Dockerfile')}
                        />
                    )}
                    {container.image_url && (
                        <InfoRow
                            label={t('overview.imageUrl', 'Image URL')}
                            value={
                                <span className="font-mono text-sm break-all">
                                    {container.image_url}
                                </span>
                            }
                        />
                    )}
                </div>
            </DialogBox>

            <DialogBox
                label={t('overview.containerConfigTitle', 'Container configuration')}
                description={t(
                    'overview.containerConfigDescription',
                    'Runtime resources and scaling settings.'
                )}
                classNames="p-4"
            >
                <div className="w-full grid grid-cols-[auto_1fr] items-center gap-4">
                    <InfoRow
                        label={t('overview.pricingType', 'Pricing model')}
                        value={
                            container.pricing_type === 'request_based'
                                ? t('overview.pricingRequestBased', 'Request-based')
                                : t('overview.pricingInstanceBased', 'Instance-based')
                        }
                    />
                    <InfoRow
                        label={t('overview.minReplicas', 'Minimum replicas')}
                        value={container.min_replicas}
                    />
                    <InfoRow
                        label={t('overview.maxReplicas', 'Maximum replicas')}
                        value={container.max_replicas}
                    />
                    <InfoRow
                        label={t('overview.lastDeployed', 'Last deployed')}
                        value={
                            container.last_deployed_at
                                ? dayjs(container.last_deployed_at).format('DD MMM YYYY, HH:mm:ss')
                                : t('overview.notDeployed', 'Not deployed yet')
                        }
                    />
                    <InfoRow
                        label={t('overview.createdAt', 'Created at')}
                        value={dayjs(container.created_at).format('DD MMM YYYY, HH:mm:ss')}
                    />
                    {container.status_message && (
                        <InfoRow
                            label={t('overview.statusMessage', 'Status message')}
                            value={
                                <span className="text-fg/68 text-sm">
                                    {container.status_message}
                                </span>
                            }
                        />
                    )}
                </div>
            </DialogBox>

            {[
                {
                    key: 'requests' as const,
                    label: t('overview.metricsRequests', 'Requests'),
                    description: t(
                        'overview.metricsRequestsDescription',
                        'Last 24 hours, sampled at 5m.'
                    ),
                    data: chartsByMetric.requests,
                    seriesLabel: t('overview.metricsRequestsSeries', 'Requests'),
                    color: 'blue.6',
                    valueFormatter: undefined as ((v: number) => string) | undefined,
                    emptyText: t(
                        'overview.metricsRequestsEmpty',
                        'No requests yet. Send traffic to see data.'
                    ),
                },
                {
                    key: 'cpu' as const,
                    label: t('overview.metricsCpu', 'CPU (millicores)'),
                    description: t(
                        'overview.metricsCpuDescription',
                        'Last 24 hours, sampled at 5m.'
                    ),
                    data: chartsByMetric.cpu,
                    seriesLabel: t('overview.metricsCpuSeries', 'CPU'),
                    color: 'teal.6',
                    valueFormatter: (v: number) => `${formatNumber(v * 1000, 0, 2)}m`,
                    emptyText: t('overview.metricsCpuEmpty', 'No CPU data yet.'),
                },
                {
                    key: 'memory' as const,
                    label: t('overview.metricsMemory', 'Memory'),
                    description: t(
                        'overview.metricsMemoryDescription',
                        'Last 24 hours, sampled at 5m.'
                    ),
                    data: chartsByMetric.memory,
                    seriesLabel: t('overview.metricsMemorySeries', 'Memory'),
                    color: 'orange.6',
                    valueFormatter: (v: number) => formatBytes(v),
                    emptyText: t('overview.metricsMemoryEmpty', 'No memory data yet.'),
                },
            ].map(
                ({
                    key,
                    label,
                    description,
                    data,
                    seriesLabel,
                    color,
                    valueFormatter,
                    emptyText,
                }) => (
                    <DialogBox key={key} label={label} description={description} classNames="p-4">
                        {isMetricsPending ? (
                            <div className="w-full h-40 flex items-center justify-center">
                                <Loader size="sm" color="var(--fg)" />
                            </div>
                        ) : metricsError ? (
                            <div className="w-full h-40 flex flex-col items-center justify-center gap-2 text-fg/68">
                                <IconChartLine size={28} />
                                <span className="text-sm">
                                    {t('overview.metricsError', 'Failed to load metrics.')}
                                </span>
                            </div>
                        ) : data.length === 0 ? (
                            <div className="w-full h-40 flex flex-col items-center justify-center gap-2 text-fg/68">
                                <IconChartLine size={28} />
                                <span className="text-sm">{emptyText}</span>
                            </div>
                        ) : (
                            <div className="w-full min-w-0 min-h-0">
                                <LineChart
                                    h={280}
                                    data={data}
                                    dataKey="date"
                                    series={[{ name: 'value', label: seriesLabel, color }]}
                                    curveType="monotone"
                                    tickLine="none"
                                    withDots={false}
                                    valueFormatter={valueFormatter}
                                />
                            </div>
                        )}
                    </DialogBox>
                )
            )}
        </div>
    );
}
